<template>
  <div>
    <HeaderNavbar :title="$t('billing.budget.title')" :breadcrumbs="listBreadcrumbs" />
    <div class="container-lead">
      <div v-if="activeBudget" class="d-flex gap-2 align-items-stretch">
        <div class="card w-100">
          <div class="card-body">
            <h3>{{ $t('billing.budget.remaining_card') }}</h3>
            <p class="fw-bold display-6 mt-3">{{ formatCount(remainingAmount) }}</p>
            <p class="d-flex align-items-center gap-1 fw-bolder text-muted">
              <IconCoins />
              <span
                >{{ formatCount(spentAmount) }} {{ $t('billing.budget.spent_of') }}
                {{ formatCount(budgetAmount) }}</span
              >
            </p>
            <div class="budget-progress mt-2">
              <div
                class="budget-progress-bar"
                :class="consumptionPercentage > 90 ? 'bg-danger' : consumptionPercentage > 70 ? 'bg-warning' : ''"
                :style="{ width: `${Math.min(consumptionPercentage, 100)}%` }" />
            </div>
            <p class="text-muted mt-1 mb-0">
              <small>{{ Math.round(consumptionPercentage) }}% {{ $t('billing.budget.consumed') }}</small>
            </p>
            <BarGraph
              v-if="remainingBudgetData.length"
              :data="remainingBudgetData"
              class="mt-3"
              height="100px"
              margin="0"
              :show-count="formatCount"
              :show-label="(l: string | number) => l" />
          </div>
        </div>
        <div class="card w-100">
          <div class="card-body d-flex flex-column h-100 gap-3">
            <h3>{{ $t('billing.budget.allocations_card') }}</h3>
            <div class="d-flex flex-column gap-2">
              <div class="allocation-card border rounded-3 flex-wrap">
                <p class="m-0 text-muted">{{ $t('billing.budget.allocations_card.unallocated') }}</p>
                <p class="m-0 text-muted text-nowrap">
                  {{ formatCount(vBudgetTree?.availability?.available_unallocated || 0) }}
                  <span class="fw-normal">{{ $t('of') }}</span>
                  {{ formatCount(vBudgetTree?.availability?.available_now || 0) }}
                </p>
              </div>

              <template v-if="firstTwoVBudgets.length">
                <div
                  class="allocation-card border rounded-3 flex-wrap"
                  v-for="vBudget in firstTwoVBudgets"
                  :key="vBudget.vbudget?.data.id">
                  <p class="m-0">{{ vBudget.vbudget?.data.name }}</p>
                  <p class="m-0 text-muted text-nowrap">
                    {{ formatCount(vBudget.availability.available_unallocated) }}
                    <span class="fw-normal">{{ $t('of') }}</span>
                    {{ formatCount(vBudget.availability.available_now) }}
                  </p>
                </div>
              </template>
              <p class="m-0 text-muted w-fit" v-if="remainingChildren">
                <small>{{ remainingChildren }} {{ $t('billing.budget.more_allocations') }}</small>
              </p>
            </div>
            <div class="text-center mt-auto mb-1">
              <RouterLink :to="{ name: 'billing.vbudgets' }" class="btn btn-white mt-2">
                <IconWallet size="16" class="me-1" />
                {{ $t('billing.budget.manage_virtual_budgets') }}
              </RouterLink>
            </div>
          </div>
        </div>
        <div class="card w-100">
          <div class="card-body">
            <h3>
              {{ activeBudget?.name ? activeBudget.name : '&lt;Budget Name&gt;' }}
            </h3>
            <p class="mt-3 mb-1" v-if="activeBudget?.description">{{ truncatedContent }}</p>
            <button class="btn btn-link ps-0" v-if="shouldShowButton" @click="showMore = !showMore">
              {{ showMore ? 'Show Less' : 'Show More' }}
            </button>
            <div class="mt-3">
              <p class="mb-2 fw-bold">{{ $t('billing.budget.budget_tab.contract') }}</p>
              <p class="m-0 fw-medium">{{ budgetDates }}</p>
            </div>
            <div v-if="activeBudget?.contracts" class="d-flex flex-wrap mt-3 gap-2">
              <a
                v-for="contract in activeBudget?.contracts.data"
                :key="contract.id"
                :href="`/v1/account/attachments/${contract?.attachment_id}/contents`"
                @click.prevent="downloadFile($event, contract?.attachment_id)"
                rel="noopener noreferrer"
                :title="contract?.attachment?.data?.filename"
                :download="contract?.attachment?.data?.filename"
                class="file-card rounded-3">
                <IconPaperclip class="me-2" />
                <span class="m-0">{{ contract?.attachment?.data?.filename }}</span>
              </a>
            </div>
            <div class="mt-3">
              <p class="mb-2 fw-bold">{{ $t('billing.budget.budget_tab.contact') }}</p>
              <a
                v-if="activeBudget?.user_id && activeBudget.user"
                class="m-0"
                :href="`mailto:${activeBudget?.user.data?.email}`">
                {{ activeBudget.user?.data?.display_name }}</a
              >
              <p v-else class="m-0 text-muted">{{ $t('billing.budget.budget_tab.no_contact') }}</p>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="text-center mt-5">{{ $t('billing.budget.no_budget') }}</p>
      </div>
      <BudgetTransactionsTable v-if="activeBudget" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import translator, { currentLanguage } from '@/locale/translator';
import { IconCoins, IconPaperclip, IconWallet } from '@tabler/icons-vue';
import BarGraph from '@/components/common/BarGraph.vue';
import type { IActiveBudget, IMonthlyBudgetBalance, IBudgetTreeNode } from '@/types/budget';
import { fetchActiveBudget, fetchBudgetTree } from '@/api/budget';
import { formatCurrency } from '@/locale/format';
import BudgetTransactionsTable from '@/components/budget/BudgetTransactionsTable.vue';
import { downloadAttachment } from '@/api/attachment';
import { formatDate } from '@/filters/dateFormat';

const activeBudget = ref<IActiveBudget>();
const vBudgetTree = ref<IBudgetTreeNode>();
const showMore = ref(false);

onMounted(async () => {
  fetchActiveBudget({ resolve: 'contracts,user' })
    .then(async (activeBudgetResponse) => {
      if (activeBudgetResponse?.data) {
        activeBudget.value = activeBudgetResponse.data;
        const budgetTreeResponse = await fetchBudgetTree(activeBudget.value.id).then(
          (response) => response.data || response
        );
        vBudgetTree.value = budgetTreeResponse.tree;
      }
    })
    .catch((error) => {
      console.error('Error fetching active budget:', error);
    });
});

/* -------------------------------- COMPUTED -------------------------------- */
const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.billing'), path: { name: 'billing' } },
  { label: translator.get('nav.billing.budget'), path: { name: 'billing.budget' } },
]);

const budgetAmount = computed(() => {
  return activeBudget.value?.amount || 0;
});

const remainingAmount = computed(() => {
  return vBudgetTree.value?.availability?.available_now ?? budgetAmount.value;
});

const spentAmount = computed(() => {
  return vBudgetTree.value?.availability?.total_spent ?? 0;
});

const consumptionPercentage = computed(() => {
  if (!budgetAmount.value) return 0;
  return (spentAmount.value / budgetAmount.value) * 100;
});

const budgetDates = computed(() => {
  const start = activeBudget.value?.startdate ? formatDate(activeBudget.value.startdate) : 'N/A';
  const end = activeBudget.value?.enddate ? formatDate(activeBudget.value.enddate) : 'N/A';
  return `${start} – ${end}`;
});

const remainingBudgetData = computed(() => {
  const locale = currentLanguage === 'en_US' ? 'en-US' : 'de-CH';
  const months = activeBudget.value?.remaining_budget?.group_by_month ?? [];
  const toMonthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();
  const currentMonth = toMonthIndex(new Date());
  const startMonth = activeBudget.value?.startdate ? toMonthIndex(new Date(activeBudget.value.startdate)) : -Infinity;
  const endMonth = activeBudget.value?.enddate ? toMonthIndex(new Date(activeBudget.value.enddate)) : Infinity;

  return months.map((m: IMonthlyBudgetBalance) => {
    const month = m.year * 12 + m.month;
    const inBudgetPeriod = month >= startMonth && month <= endMonth;
    return {
      // months that have not started yet report the current balance
      count: inBudgetPeriod ? m.budget : 0,
      label: new Date(m.year, m.month, 1).toLocaleDateString(locale, { month: 'short', year: 'numeric' }),
      highlighted: inBudgetPeriod && month <= currentMonth,
      countLabel: inBudgetPeriod ? undefined : translator.get('billing.budget.chart.no_budget'),
    };
  });
});

const firstTwoVBudgets = computed(() => {
  const allocations = [] as IBudgetTreeNode[];
  for (const child of vBudgetTree.value?.children || []) {
    if (child.type === 'vbudget') {
      allocations.push(child);
    }
  }

  // sort by available allocation in descending order and return the first two
  return allocations
    .sort((a, b) => (b.availability.available_now || 0) - (a.availability.available_now || 0))
    .slice(0, 2);
});

const remainingChildren = computed(() => {
  const childrenCount = (vBudgetTree.value?.children || []).length;

  return childrenCount - 2 > 0 ? childrenCount - 2 : 0;
});

const truncatedContent = computed(() => {
  if (showMore.value || (activeBudget?.value?.description ?? '').length <= 100) {
    return activeBudget?.value?.description;
  }
  return activeBudget.value?.description.slice(0, 100) + '...';
});

const shouldShowButton = computed(() => {
  return (activeBudget?.value?.description ?? '').length > 100;
});

/* -------------------------------- METHODS -------------------------------- */

function formatCount(count: number) {
  return formatCurrency(count, activeBudget.value?.currency);
}

async function downloadFile(event: MouseEvent, attachmentId: number | undefined) {
  if (!attachmentId) {
    console.error('Attachment ID is undefined');
    return;
  }
  event.preventDefault();
  const target = event.currentTarget as HTMLAnchorElement;

  try {
    const blob = await downloadAttachment(attachmentId);

    const downloadUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = target.download || target.title || 'download';

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('API download failed:', error);

    window.open(target.href, '_blank');
  }
}
</script>
<style scoped>
.allocation-card {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  font-weight: bold;
}

.budget-progress {
  height: 8px;
  background-color: var(--tblr-border-color);
  border-radius: 4px;
  overflow: hidden;
}

.budget-progress-bar {
  height: 100%;
  background-color: var(--tblr-success);
  border-radius: 4px;
  transition: width 0.4s ease;
}

.budget-progress-bar.bg-warning {
  background-color: var(--tblr-warning);
}

.budget-progress-bar.bg-danger {
  background-color: var(--tblr-danger);
}

.file-card {
  display: flex;
  align-items: center;
  padding: 5px 10px;
  margin: 0;
  font-weight: 500;
  background-color: #e3e3e3;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}
</style>
